import React from 'react';
import PropTypes from 'prop-types';

import RoundedButton from '../../../common/RoundedButton';

import AppError from '../../../services/AppError';
import {
  PaymentIntentNotCompletedError,
  SetupIntentNotCompletedError,
  StripeError
} from '../../../services/Stripe';
import tr from '../../../localization/translate';

function errorText(text, className = '') {
  return (<p className={`text tallish_lines ${className}`}>{ text }</p>);
}

function genericButton(onClick) {
  return {
    content: tr('common.retry_button'),
    action: { onClick }
  };
}

function genericError(onRetryPayment, text) {
  return {
    content: errorText(text),
    button: genericButton(onRetryPayment)
  };
}

function handleStripeError(error, onRetryPayment) {
  if (error instanceof StripeError) {
    if (error.type === 'card_error') {
      if (error.decline_code) {
        switch (error.decline_code) { // eslint-disable-line default-case
          case 'incorrect_address':
          case 'incorrect_number':
          case 'incorrect_cvc':
          case 'incorrect_zip':
          case 'invalid_cvc':
          case 'invalid_expiry_month':
          case 'invalid_expiry_year':
          case 'invalid_number':
            return genericError(onRetryPayment, tr('pay_form.errors.check_card_details'));

          case 'approve_with_id':
          case 'issuer_not_available':
          case 'processing_error':
          case 'reenter_transaction':
          case 'try_again_later':
            return genericError(onRetryPayment, tr('pay_form.errors.should_try_again'));

          case 'currency_not_supported':
            return genericError(onRetryPayment, tr('pay_form.errors.currency_not_supported'));

          case 'do_not_try_again':
            return genericError(onRetryPayment, tr('pay_form.errors.do_not_try_again'));

          case 'expired_card':
            return genericError(onRetryPayment, tr('pay_form.errors.expired_card'));

          case 'insufficient_funds':
            return genericError(onRetryPayment, tr('pay_form.errors.insufficient_funds'));

          case 'withdrawal_count_limit_exceeded':
            return genericError(
              onRetryPayment,
              tr('pay_form.errors.withdrawal_count_limit_exceeded')
            );
        }
      }

      if (error.code) {
        switch (error.code) { // eslint-disable-line default-case
          case 'incorrect_address':
          case 'incorrect_number':
          case 'incorrect_cvc':
          case 'incorrect_zip':
          case 'invalid_cvc':
          case 'invalid_expiry_month':
          case 'invalid_expiry_year':
          case 'invalid_number':
            return genericError(onRetryPayment, tr('pay_form.errors.check_card_details'));

          case 'expired_card':
            return genericError(onRetryPayment, tr('pay_form.errors.expired_card'));
        }
      }

      return genericError(onRetryPayment, tr('pay_form.errors.contact_issuer_error'));
    }

    return genericError(onRetryPayment, tr('pay_form.errors.stripe_api_error'));
  }

  if (error instanceof SetupIntentNotCompletedError ||
      error instanceof PaymentIntentNotCompletedError
  ) {
    return genericError(onRetryPayment, tr('pay_form.errors.authentication_not_completed'));
  }

  return null;
}

function processingStatusToErrorText(requestProcessingStatus) {
  switch (requestProcessingStatus) {
    case 'PRE_CHARGE':
    case 'CHARGE': return tr('pay_form.errors.processing_status.pre_charge');

    case 'REFUNDED': return tr('pay_form.errors.processing_status.refunded');

    case 'POST_CHARGE':
    case 'REFUND_FAILED': return tr('pay_form.errors.processing_status.not_refunded');

    default: return '';
  }
}

function processingStatusError(button, errorTextContent, requestProcessingStatus) {
  return {
    content: (
      <>
        { errorText(errorTextContent) }
        { errorText(processingStatusToErrorText(requestProcessingStatus), 'top_margin_16') }
      </>
    ),
    button
  };
}


function genericProcessingStatusError(onRetryPayment, requestProcessingStatus) {
  return processingStatusError(
    genericButton(onRetryPayment),
    tr('pay_form.errors.unknown_error'),
    requestProcessingStatus
  );
}

function getGenericErrorDetails(error, onRetryPayment) {
  if (error instanceof AppError && error.requestProcessingStatus !== '') {
    return genericProcessingStatusError(onRetryPayment, error.requestProcessingStatus);
  }

  return genericError(onRetryPayment, tr('pay_form.errors.unknown_error'));
}

function getErrorDetails(error, onRetryPayment, onHandleNonStripeError) {
  const stripeErrorDetails = handleStripeError(error, onRetryPayment);
  if (stripeErrorDetails) {
    return stripeErrorDetails;
  }

  const nonStripeErrorDetails = onHandleNonStripeError(
    error,
    onRetryPayment,
    errorText,
    genericButton,
    genericError,
    processingStatusError
  );
  if (nonStripeErrorDetails) {
    return nonStripeErrorDetails;
  }

  return getGenericErrorDetails(error, onRetryPayment);
}

export default function Error({ className, error, onRetryPayment, onHandleNonStripeError }) {
  const errorDetails = getErrorDetails(error, onRetryPayment, onHandleNonStripeError);

  return (
    <div className={className}>
      { errorDetails.content }

      <RoundedButton className="top_margin_36" {...errorDetails.button.action}>
        { errorDetails.button.content }
      </RoundedButton>
    </div>
  );
}

Error.propTypes = {
  className: PropTypes.string,
  error: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onRetryPayment: PropTypes.func.isRequired,
  onHandleNonStripeError: PropTypes.func.isRequired
};

Error.defaultProps = { className: '' };
